/**
 * A WebSocket that comes back. (aweb's, ported from usgaweb.)
 *
 * The two sockets in this app - the worklist and the exam media feed - were plain
 * `new WebSocket(...)` with an `onmessage` handler and nothing else. No
 * `onclose`, so a dropped connection was never re-established: the page kept working and silently
 * stopped updating.
 *
 * That was survivable while one process served everything: the socket only died when the server
 * restarted. With two copies behind the load balancer it dies on every deploy, each copy draining
 * in turn - so without this, every open worklist and exam stops updating after a deploy, with no
 * error and nothing in any log.
 *
 * WHAT IT HANDLES
 *
 *   server went away      a drain, a deploy, a rolling restart
 *   network blipped       wifi, VPN
 *   laptop slept          the socket is dead but no close event fires until the OS notices, so
 *                         the tab becoming visible again triggers a check
 *   session ended         a tab left open past its session has no cookie, and the server refuses
 *                         its socket. Retrying every few seconds would go on for as long as the
 *                         tab stays open, so after a refused attempt it asks the server whether
 *                         it is still signed in, and if not it pauses: one check a minute (and
 *                         one whenever the tab is shown again) until someone signs in again
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *
 *   Queue or replay messages. Every message these sockets carry is a "something changed, go and
 *   look" nudge, so the caller re-reads after a REconnect instead (onOpen's `reconnected`).
 */

export type SocketState = 'connecting' | 'open' | 'closed';

export class ReconnectingSocket {
  private ws: WebSocket | null = null;
  private stopped = false;
  private attempt = 0;
  private opened = false; // has any connection opened yet: the first open is not a reconnect
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly onVisible: () => void;
  private readonly onOnline: () => void;

  /** Backoff, capped: a drain takes ~25s and a rolling restart of two copies about a minute, so
   *  retries stay frequent enough to reconnect promptly without a thundering herd. */
  private static readonly BASE_MS = 500;
  private static readonly MAX_MS = 10000;

  /** While signed out: how often to look again. Cheap - a GET the server answers from the session. */
  private static readonly SIGNED_OUT_CHECK_MS = 60000;
  private paused = false;

  /**
   * @param url        Recomputed on every attempt, not captured once.
   * @param onMessage  Called for each message.
   * @param label      For the console.
   * @param onOpen     Called after every successful connect, with `reconnected` false the first
   *                   time and true after a drop. A reconnect is where a caller re-reads whatever
   *                   it may have missed while disconnected; the first open is not - the page has
   *                   just loaded it.
   */
  constructor(
    private readonly url: () => string,
    private readonly onMessage: (ev: MessageEvent) => void,
    private readonly label = 'socket',
    private readonly onOpen?: (reconnected: boolean) => void
  ) {
    this.onVisible = () => {
      if (document.visibilityState === 'visible') this.ensureConnected();
    };
    this.onOnline = () => this.ensureConnected();
    document.addEventListener('visibilitychange', this.onVisible);
    window.addEventListener('online', this.onOnline);

    this.connect();
  }

  get state(): SocketState {
    if (this.stopped) return 'closed';
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return 'open';
    return 'connecting';
  }

  /** Deliberate close. Stops reconnecting - distinguishable from the connection dropping. */
  close(): void {
    this.stopped = true;
    this.clearTimer();
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('online', this.onOnline);
    if (this.ws) {
      // Drop the handlers first: closing fires onclose, which would otherwise schedule a
      // reconnect for a socket being discarded.
      this.ws.onopen = this.ws.onmessage = this.ws.onerror = this.ws.onclose = null;
      try {
        this.ws.close(1000, 'client done');
      } catch {
        /* already gone */
      }
      this.ws = null;
    }
  }

  private ensureConnected(): void {
    if (this.stopped) return;
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    )
      return;
    this.clearTimer();
    this.attempt = 0; // a user-visible event is not a failure - retry at once
    this.connect();
  }

  private connect(): void {
    if (this.stopped) return;
    try {
      this.ws = new WebSocket(this.url());
    } catch {
      // a malformed URL throws on construction: a failed attempt, not the caller's exception
      this.scheduleReconnect(false);
      return;
    }

    let didOpen = false; // this attempt, as opposed to `opened` (any attempt ever)
    this.ws.onopen = () => {
      didOpen = true;
      this.attempt = 0;
      this.paused = false;
      const reconnected = this.opened;
      this.opened = true;
      if (reconnected) console.log(`[${this.label}] socket reconnected`);
      if (this.onOpen) this.onOpen(reconnected);
    };
    this.ws.onmessage = (ev) => this.onMessage(ev);
    // onerror is always followed by onclose, which reconnects; doing it in both would schedule
    // two attempts for one failure.
    this.ws.onerror = () => {
      /* handled by onclose */
    };
    this.ws.onclose = () => {
      this.ws = null;
      // An attempt that never opened may have been refused for want of a session: ask before
      // trying again. A socket that was open and dropped (a deploy, the network) just reconnects.
      if (!this.stopped) this.scheduleReconnect(!didOpen);
    };
  }

  /**
   * After a refused attempt: reconnect if this tab is still signed in, otherwise pause. Every
   * socket path has a plain GET twin on the server that answers 401 without a session, and fetch()
   * rather than HttpClient keeps the app's own 401 handling out of it - the tab is not touched.
   */
  private async checkSessionThenConnect(): Promise<void> {
    if (this.stopped) return;
    const signedOut = await this.signedOut();
    // A visibility or online event may have connected while the check was in flight.
    if (this.stopped || this.ws || this.timer) return;
    if (!signedOut) {
      this.paused = false;
      this.connect();
      return;
    }
    if (!this.paused) console.log(`[${this.label}] signed out - pausing reconnects`);
    this.paused = true;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.checkSessionThenConnect();
    }, ReconnectingSocket.SIGNED_OUT_CHECK_MS);
  }

  private async signedOut(): Promise<boolean> {
    try {
      const res = await fetch(this.url().replace(/^ws/, 'http'), { cache: 'no-store' });
      return res.status === 401;
    } catch {
      return false; // unreachable is a network problem, not a signed-out tab: keep retrying
    }
  }

  private scheduleReconnect(checkSession: boolean): void {
    if (this.stopped || this.timer) return;
    const backoff = Math.min(
      ReconnectingSocket.MAX_MS,
      ReconnectingSocket.BASE_MS * Math.pow(2, this.attempt)
    );
    // Jitter: a drain drops every client at the same instant, and they would otherwise all retry
    // in lockstep against the copy still serving.
    const delay = backoff / 2 + Math.random() * (backoff / 2);
    this.attempt++;
    this.timer = setTimeout(() => {
      this.timer = null;
      if (checkSession) void this.checkSessionThenConnect();
      else this.connect();
    }, delay);
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

/** The ws:// or wss:// origin of this page - the host and port it was served from, so it follows
 *  the balancer without configuration. */
export function socketOrigin(): string {
  return (
    (window.location.protocol === 'https:' ? 'wss://' : 'ws://') +
    window.location.hostname +
    (window.location.port ? ':' + window.location.port : '')
  );
}
